import React, { useState, useEffect, useMemo, startTransition } from 'react';
import { toast } from 'react-hot-toast';
import '../../context/AuthContext.css';
import { Card } from '../../components/ui/Card';
import { LineChart, BarChart } from '../../components/charts';
import AccountSwitcher from '../../components/AccountSwitcher';
import { useAccounts } from '../../context/AccountContext';
import { 
  DollarSign, 
  TrendingUp, 
  MousePointerClick, 
  Target,
  Users,
  Image,
  MessageCircle,
  Share2,
  ShoppingCart,
  Eye,
  ThumbsUp,
  BarChart3,
  RefreshCw,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpRight,
  PieChart,
  Hash,
  Play,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { YATAS_GROUP_BRAND_IDS, filterRowsByYatasCampaignName } from '../../utils/yatasCampaignNameFilter';
import {
  buildMetaMetricsFromCampaignRows,
  processDailyTrendFromCampaignRows,
  deriveMetaCampaignTables,
} from '../../utils/metaMetricsFromCampaignRows';

// Special brand ID for dummy data
const DUMMY_DATA_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

// Generate dummy data for the special brand
const generateDummyMetaData = () => {
  const platforms = ['facebook', 'instagram'];
  const placements = {
    facebook: ['Feed', 'Stories', 'Reels', 'Marketplace', 'Right Column', 'Video Feeds'],
    instagram: ['Feed', 'Stories', 'Reels', 'Explore', 'Shop', 'Profile Feed']
  };
  
  const dummyPlacements = [];
  platforms.forEach(platform => {
    placements[platform].forEach(placement => {
      const spend = Math.floor(Math.random() * 15000) + 2000;
      const impressions = Math.floor(Math.random() * 500000) + 50000;
      const clicks = Math.floor(Math.random() * 15000) + 1000;
      const conversions = Math.floor(Math.random() * 300) + 20;
      const conversionValue = conversions * (Math.floor(Math.random() * 150) + 80);
      
      dummyPlacements.push({
        platform,
        publisherPlatform: platform,
        placementPosition: placement,
        platformPosition: placement,
        devicePlatform: 'mobile',
        spend,
        impressions,
        clicks,
        conversions,
        purchaseConversions: conversions,
        conversionValue,
        purchaseValue: conversionValue,
        ctr: (clicks / impressions * 100),
        cpc: spend / clicks,
        cpa: spend / conversions,
        roas: conversionValue / spend
      });
    });
  });
  
  return dummyPlacements;
};

const generateDummyCampaigns = () => {
  const campaignNames = [
    { name: 'Demo - Kış İndirimi 2024', objective: 'CONVERSIONS' },
    { name: 'Demo - Yeni Sezon Kadın', objective: 'CONVERSIONS' },
    { name: 'Demo - Erkek Koleksiyonu', objective: 'CONVERSIONS' },
    { name: 'Demo - Çocuk Giyim', objective: 'CONVERSIONS' },
    { name: 'Demo - Outlet Kampanyası', objective: 'CONVERSIONS' },
    { name: 'Demo - Brand Awareness', objective: 'BRAND_AWARENESS' },
    { name: 'Demo - Retargeting - Website', objective: 'CONVERSIONS' },
    { name: 'Demo - App Install', objective: 'APP_INSTALLS' },
    { name: 'Demo - Black Friday', objective: 'CONVERSIONS' },
    { name: 'Demo - Yılbaşı Özel', objective: 'CONVERSIONS' }
  ];
  
  return campaignNames.map((campaign, idx) => {
    const spend = Math.floor(Math.random() * 25000) + 5000;
    const impressions = Math.floor(Math.random() * 800000) + 100000;
    const clicks = Math.floor(Math.random() * 25000) + 2000;
    const conversions = Math.floor(Math.random() * 500) + 50;
    const revenue = conversions * (Math.floor(Math.random() * 200) + 100);
    
    return {
      id: `2384756389${idx}`,
      name: campaign.name,
      objective: campaign.objective,
      spend,
      impressions,
      clicks,
      conversions,
      revenue,
      cpc: spend / clicks,
      roas: revenue / spend,
      status: 'active'
    };
  });
};

const generateDummyAdSets = () => {
  const adSetNames = [
    { name: 'LAL - Kadın 25-44 Türkiye', campaign: 'Demo - Kış İndirimi 2024' },
    { name: 'INT - Broad Audience', campaign: 'Demo - Kış İndirimi 2024' },
    { name: 'RET - Website Visitors 30D', campaign: 'Demo - Retargeting - Website' },
    { name: 'RET - Add to Cart', campaign: 'Demo - Retargeting - Website' },
    { name: 'LAL - Erkek 18-35', campaign: 'Demo - Erkek Koleksiyonu' },
    { name: 'INT - Fashion Interested', campaign: 'Demo - Yeni Sezon Kadın' },
    { name: 'LAL - Çocuk Ebeveyn', campaign: 'Demo - Çocuk Giyim' },
    { name: 'INT - Brand Fans', campaign: 'Demo - Brand Awareness' },
    { name: 'RET - Purchasers', campaign: 'Demo - Outlet Kampanyası' },
    { name: 'LAL - High Value Customers', campaign: 'Demo - Black Friday' },
    { name: 'INT - Gift Shoppers', campaign: 'Demo - Yılbaşı Özel' },
    { name: 'APP - Mobile Users', campaign: 'Demo - App Install' }
  ];
  
  return adSetNames.map((adset, idx) => {
    const spend = Math.floor(Math.random() * 12000) + 2000;
    const impressions = Math.floor(Math.random() * 400000) + 50000;
    const clicks = Math.floor(Math.random() * 12000) + 1000;
    const conversions = Math.floor(Math.random() * 200) + 20;
    const revenue = conversions * (Math.floor(Math.random() * 180) + 90);
    
    return {
      id: `3847569384${idx}`,
      name: adset.name,
      campaignName: adset.campaign,
      spend,
      impressions,
      clicks,
      conversions,
      revenue,
      cpc: spend / clicks,
      roas: revenue / spend,
      status: 'active'
    };
  });
};

const generateDummyAds = () => {
  const adNames = [
    { name: 'Carousel - Kış Ürünleri', adset: 'LAL - Kadın 25-44 Türkiye', campaign: 'Demo - Kış İndirimi 2024' },
    { name: 'Video - Sezon Tanıtım 15s', adset: 'LAL - Kadın 25-44 Türkiye', campaign: 'Demo - Kış İndirimi 2024' },
    { name: 'Single Image - %50 İndirim', adset: 'INT - Broad Audience', campaign: 'Demo - Kış İndirimi 2024' },
    { name: 'Collection - Kaban Koleksiyonu', adset: 'LAL - Kadın 25-44 Türkiye', campaign: 'Demo - Kış İndirimi 2024' },
    { name: 'DPA - Website Visitors', adset: 'RET - Website Visitors 30D', campaign: 'Demo - Retargeting - Website' },
    { name: 'DPA - Cart Abandoners', adset: 'RET - Add to Cart', campaign: 'Demo - Retargeting - Website' },
    { name: 'Video - Erkek Casual', adset: 'LAL - Erkek 18-35', campaign: 'Demo - Erkek Koleksiyonu' },
    { name: 'Carousel - Yeni Gelenler', adset: 'INT - Fashion Interested', campaign: 'Demo - Yeni Sezon Kadın' },
    { name: 'Single Image - Çocuk İndirim', adset: 'LAL - Çocuk Ebeveyn', campaign: 'Demo - Çocuk Giyim' },
    { name: 'Video - Brand Story', adset: 'INT - Brand Fans', campaign: 'Demo - Brand Awareness' },
    { name: 'Carousel - Outlet Ürünler', adset: 'RET - Purchasers', campaign: 'Demo - Outlet Kampanyası' },
    { name: 'Video - Black Friday Teaser', adset: 'LAL - High Value Customers', campaign: 'Demo - Black Friday' },
    { name: 'Carousel - Yılbaşı Hediyeleri', adset: 'INT - Gift Shoppers', campaign: 'Demo - Yılbaşı Özel' },
    { name: 'Video - App Download CTA', adset: 'APP - Mobile Users', campaign: 'Demo - App Install' },
    { name: 'Reels - Trend Ürünler', adset: 'INT - Fashion Interested', campaign: 'Demo - Yeni Sezon Kadın' }
  ];
  
  return adNames.map((ad, idx) => {
    const spend = Math.floor(Math.random() * 6000) + 500;
    const impressions = Math.floor(Math.random() * 200000) + 20000;
    const clicks = Math.floor(Math.random() * 6000) + 400;
    const conversions = Math.floor(Math.random() * 100) + 5;
    const revenue = conversions * (Math.floor(Math.random() * 200) + 80);
    
    return {
      id: `4857693841${idx}`,
      name: ad.name,
      adsetName: ad.adset,
      campaignName: ad.campaign,
      spend,
      impressions,
      clicks,
      conversions,
      revenue,
      cpc: spend / clicks,
      roas: revenue / spend,
      status: 'active'
    };
  });
};

const generateDummyDailyData = () => {
  const labels = [];
  const spend = [];
  const revenue = [];
  const impressions = [];
  const clicks = [];
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    labels.push(date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }));
    
    const daySpend = Math.floor(Math.random() * 8000) + 3000;
    const dayRevenue = daySpend * (1.5 + Math.random() * 2);
    
    spend.push(daySpend);
    revenue.push(Math.floor(dayRevenue));
    impressions.push(Math.floor(Math.random() * 150000) + 50000);
    clicks.push(Math.floor(Math.random() * 5000) + 1000);
  }
  
  return { labels, spend, revenue, impressions, clicks };
};

export default function MetaOverview() {
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('META_ADS');
  const [loading, setLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState('30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [adSets, setAdSets] = useState([]);
  const [ads, setAds] = useState([]);
  const [rawMetaCampaignRows, setRawMetaCampaignRows] = useState([]);
  const [metaAccountFilter, setMetaAccountFilter] = useState('all');
  const [yatasCampaignFilter, setYatasCampaignFilter] = useState('all');
  const [activeSection, setActiveSection] = useState('campaigns'); // Aktif section
  const [metaData, setMetaData] = useState(null);
  // Loaded from monthly_targets_v2 (channel_key=META_ADS, current month). null
  // means no target set in DB — UI shows an inline input instead of a fake default.
  const [targetBudget, setTargetBudget] = useState(null);
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [savingTargetBudget, setSavingTargetBudget] = useState(false);

  // Load Meta spend_target from monthly_targets_v2 for the current month
  useEffect(() => {
    let cancelled = false;
    const loadMonthlyTarget = async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth() + 1;
        const res = await fetch(
          `https://api.glowytics.com/api/monthly-targets?year=${y}&month=${m}&channel_key=META_ADS`,
          { credentials: 'include', headers: { 'X-Brand-ID': brandId } }
        );
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const rows = Array.isArray(json) ? json : (Array.isArray(json?.data) ? json.data : []);
        if (rows.length > 0) {
          const row = rows.find(r => !r.account_id) || rows[0];
          if (row?.spend_target != null && !cancelled) {
            setTargetBudget(Number(row.spend_target));
          }
        }
      } catch (e) {
        console.warn('[MetaOverview] failed to load monthly target:', e.message);
      }
    };
    loadMonthlyTarget();
    const handler = () => loadMonthlyTarget();
    window.addEventListener('storage', handler);
    window.addEventListener('brandChanged', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', handler);
      window.removeEventListener('brandChanged', handler);
    };
  }, []);

  // Persist Meta target budget to monthly_targets_v2 (current month, META_ADS)
  const saveMonthlyTarget = async (numValue) => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) { toast.error('No brand selected'); return; }
      setSavingTargetBudget(true);
      const now = new Date();
      const res = await fetch('https://api.glowytics.com/api/monthly-targets', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          channel_key: 'META_ADS',
          spend_target: numValue,
          revenue_source: 'ga4'
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setTargetBudget(numValue);
      toast.success('Monthly target saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save target');
    } finally {
      setSavingTargetBudget(false);
    }
  };
  const [fbPlacementSort, setFbPlacementSort] = useState('spend'); // spend, roas, ctr
  const [igPlacementSort, setIgPlacementSort] = useState('spend'); // spend, roas, ctr
  
  // Format a Date to YYYY-MM-DD in local time to avoid timezone shifts
  const formatLocalDateYMD = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const showYatasCampaignFilter = brandId && YATAS_GROUP_BRAND_IDS.has(brandId);

  // Build account name map and options from campaign data (accountName comes from backend)
  const accountNameMap = useMemo(() => {
    const map = {};
    rawMetaCampaignRows.forEach(r => {
      if (r.accountId && r.accountName) {
        map[r.accountId] = r.accountName;
      }
    });
    return map;
  }, [rawMetaCampaignRows]);

  const metaAccountOptions = useMemo(() => {
    const ids = new Set();
    rawMetaCampaignRows.forEach(r => { if (r.accountId) ids.add(r.accountId); });
    return Array.from(ids).sort();
  }, [rawMetaCampaignRows]);

  // Filter metaData (placement) by selected account
  const filteredMetaData = useMemo(() => {
    if (!metaData || !Array.isArray(metaData) || metaAccountFilter === 'all') return metaData;
    return metaData.filter(r => r.accountId === metaAccountFilter);
  }, [metaData, metaAccountFilter]);

  // Filter campaign rows by selected account
  const accountFilteredCampaignRows = useMemo(() => {
    if (metaAccountFilter === 'all') return rawMetaCampaignRows;
    return rawMetaCampaignRows.filter(r => r.accountId === metaAccountFilter);
  }, [rawMetaCampaignRows, metaAccountFilter]);

  const filteredMetaCampaignRows = useMemo(() => {
    const bid = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
    return filterRowsByYatasCampaignName(accountFilteredCampaignRows, bid, yatasCampaignFilter, (r) => r.campaignName);
  }, [accountFilteredCampaignRows, yatasCampaignFilter]);

  const rowsForTables = useMemo(() => {
    const bid = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
    if (YATAS_GROUP_BRAND_IDS.has(bid) && yatasCampaignFilter !== 'all') {
      return filteredMetaCampaignRows;
    }
    return accountFilteredCampaignRows;
  }, [accountFilteredCampaignRows, filteredMetaCampaignRows, yatasCampaignFilter]);
  
  // Computed metrics from API data
  const computedMetrics = useMemo(() => {
    const bid = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
    const yatasFilterActive = YATAS_GROUP_BRAND_IDS.has(bid) && yatasCampaignFilter !== 'all';
    const useCampaignSliceFiltered =
      yatasFilterActive && filteredMetaCampaignRows.length > 0;
    const useCampaignSliceEmpty =
      yatasFilterActive && accountFilteredCampaignRows.length > 0 && filteredMetaCampaignRows.length === 0;

    if (useCampaignSliceEmpty) {
      return buildMetaMetricsFromCampaignRows([]);
    }
    if (useCampaignSliceFiltered) {
      return buildMetaMetricsFromCampaignRows(filteredMetaCampaignRows);
    }

    if (!filteredMetaData || !Array.isArray(filteredMetaData) || filteredMetaData.length === 0) {
      return {
        totalSpend: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalPurchaseConversions: 0,
        totalPurchaseValue: 0,
        averageCtr: 0,
        averageCpc: 0,
        roas: 0,
        cpa: 0,
        // Platform specific metrics
        platforms: {
          facebook: {
            spend: 0,
            impressions: 0,
            clicks: 0,
            purchaseConversions: 0,
            purchaseValue: 0,
            ctr: 0,
            cpc: 0,
            cpa: 0,
            roas: 0,
            placements: []
          },
          instagram: {
            spend: 0,
            impressions: 0,
            clicks: 0,
            purchaseConversions: 0,
            purchaseValue: 0,
            ctr: 0,
            cpc: 0,
            cpa: 0,
            roas: 0,
            placements: []
          }
        }
      };
    }

    // Calculate totals from metaData - UPDATED for placement endpoint
    const totals = filteredMetaData.reduce((acc, item) => {
      return {
        totalSpend: acc.totalSpend + Number(item.spend || 0),
        totalImpressions: acc.totalImpressions + Number(item.impressions || 0),
        totalClicks: acc.totalClicks + Number(item.clicks || 0),
        totalPurchaseConversions: acc.totalPurchaseConversions + Number(item.purchaseConversions || item.conversions || 0),
        totalPurchaseValue: acc.totalPurchaseValue + Number(item.purchaseValue || item.conversionValue || 0)
      };
    }, {
      totalSpend: 0,
      totalImpressions: 0,
      totalClicks: 0,
      totalPurchaseConversions: 0,
      totalPurchaseValue: 0
    });

    // Group data by publisher platform and placement
    const platformData = {
      facebook: {
        spend: 0,
        impressions: 0,
        clicks: 0,
        purchaseConversions: 0,
        purchaseValue: 0,
        placements: {}
      },
      instagram: {
        spend: 0,
        impressions: 0,
        clicks: 0,
        purchaseConversions: 0,
        purchaseValue: 0,
        placements: {}
      }
    };

    // Process each record to build platform and placement data - UPDATED for placement endpoint
    filteredMetaData.forEach(item => {
      const platform = item.platform?.toLowerCase() || item.publisherPlatform?.toLowerCase() || 'unknown';
      const placement = item.placementPosition || item.platformPosition || 'unknown';
      
      // Skip if not facebook or instagram
      if (platform !== 'facebook' && platform !== 'instagram') return;
      
      // Add to platform totals (data is already aggregated from backend)
      platformData[platform].spend += Number(item.spend || 0);
      platformData[platform].impressions += Number(item.impressions || 0);
      platformData[platform].clicks += Number(item.clicks || 0);
      platformData[platform].purchaseConversions += Number(item.purchaseConversions || item.conversions || 0);
      platformData[platform].purchaseValue += Number(item.purchaseValue || item.conversionValue || 0);
      
      // Initialize placement if not exists
      if (!platformData[platform].placements[placement]) {
        platformData[platform].placements[placement] = {
          name: placement,
          spend: 0,
          impressions: 0,
          clicks: 0,
          purchaseConversions: 0,
          purchaseValue: 0
        };
      }
      
      // Add to placement totals (data is already aggregated by placement from backend)
      platformData[platform].placements[placement].spend += Number(item.spend || 0);
      platformData[platform].placements[placement].impressions += Number(item.impressions || 0);
      platformData[platform].placements[placement].clicks += Number(item.clicks || 0);
      platformData[platform].placements[placement].purchaseConversions += Number(item.purchaseConversions || item.conversions || 0);
      platformData[platform].placements[placement].purchaseValue += Number(item.purchaseValue || item.conversionValue || 0);
    });

    // Calculate derived metrics for each platform
    const platforms = {};
    
    // Process Facebook data
    if (platformData.facebook) {
      const fb = platformData.facebook;
      platforms.facebook = {
        spend: fb.spend,
        impressions: fb.impressions,
        clicks: fb.clicks,
        purchaseConversions: fb.purchaseConversions,
        purchaseValue: fb.purchaseValue,
        ctr: fb.impressions > 0 ? (fb.clicks / fb.impressions * 100) : 0,
        cpc: fb.clicks > 0 ? (fb.spend / fb.clicks) : 0,
        cpa: fb.purchaseConversions > 0 ? (fb.spend / fb.purchaseConversions) : 0,
        roas: fb.spend > 0 ? (fb.purchaseValue / fb.spend) : 0,
        // Convert placements object to array and calculate metrics
        placements: Object.values(fb.placements).map(p => ({
          name: p.name,
          spend: p.spend,
          impressions: p.impressions,
          clicks: p.clicks,
          conversions: p.purchaseConversions,
          revenue: p.purchaseValue,
          ctr: p.impressions > 0 ? (p.clicks / p.impressions * 100) : 0,
          cpc: p.clicks > 0 ? (p.spend / p.clicks) : 0,
          roas: p.spend > 0 ? (p.purchaseValue / p.spend) : 0
        })).sort((a, b) => b.spend - a.spend) // Sort by spend descending
      };
    }
    
    // Process Instagram data (similar to Facebook)
    if (platformData.instagram) {
      const ig = platformData.instagram;
      platforms.instagram = {
        spend: ig.spend,
        impressions: ig.impressions,
        clicks: ig.clicks,
        purchaseConversions: ig.purchaseConversions,
        purchaseValue: ig.purchaseValue,
        ctr: ig.impressions > 0 ? (ig.clicks / ig.impressions * 100) : 0,
        cpc: ig.clicks > 0 ? (ig.spend / ig.clicks) : 0,
        cpa: ig.purchaseConversions > 0 ? (ig.spend / ig.purchaseConversions) : 0,
        roas: ig.spend > 0 ? (ig.purchaseValue / ig.spend) : 0,
        // Convert placements object to array and calculate metrics
        placements: Object.values(ig.placements).map(p => ({
          name: p.name,
          spend: p.spend,
          impressions: p.impressions,
          clicks: p.clicks,
          conversions: p.purchaseConversions,
          revenue: p.purchaseValue,
          ctr: p.impressions > 0 ? (p.clicks / p.impressions * 100) : 0,
          cpc: p.clicks > 0 ? (p.spend / p.clicks) : 0,
          roas: p.spend > 0 ? (p.purchaseValue / p.spend) : 0
        })).sort((a, b) => b.spend - a.spend) // Sort by spend descending
      };
    }

    // Calculate derived metrics
    const averageCtr = totals.totalImpressions > 0 
      ? (totals.totalClicks / totals.totalImpressions * 100)
      : 0;
      
    const averageCpc = totals.totalClicks > 0 
      ? (totals.totalSpend / totals.totalClicks)
      : 0;
      
    const roas = totals.totalSpend > 0 
      ? (totals.totalPurchaseValue / totals.totalSpend)
      : 0;
      
    // Calculate CPA (Cost Per Acquisition)
    const cpa = totals.totalPurchaseConversions > 0
      ? (totals.totalSpend / totals.totalPurchaseConversions)
      : 0;

    return {
      ...totals,
      averageCtr,
      averageCpc,
      roas,
      cpa,
      platforms
    };
  }, [filteredMetaData, filteredMetaCampaignRows, yatasCampaignFilter, accountFilteredCampaignRows.length]);

  useEffect(() => {
    if (!rawMetaCampaignRows.length) return;
    const t = deriveMetaCampaignTables(rowsForTables);
    setCampaigns(t.campaigns);
    setAdSets(t.adSets);
    setAds(t.ads);
  }, [rowsForTables, rawMetaCampaignRows.length]);

  const [data, setData] = useState({
    summary: {
      spend: 83980,
      impressions: 2400000,
      reach: 845000,
      clicks: 125000,
      conversions: 2227,
      revenue: 217348,
      roas: 2.59
    },
    platforms: {
      facebook: { 
        spend: 45230, 
        conversions: 1245, 
        revenue: 108552,
        impressions: 1320000,
        ctr: 2.8,
        cpc: 1.21,
        cpa: 36.3,
        roas: 2.4,
        placements: [
          { 
            name: "Feed", 
            spend: 27138, 
            impressions: 792000, 
            ctr: 3.1, 
            conversions: 747, 
            roas: 2.6,
            revenue: 70559
          },
          { 
            name: "Stories", 
            spend: 9046, 
            impressions: 330000, 
            ctr: 2.3, 
            conversions: 249, 
            roas: 1.9,
            revenue: 17187
          }, 
          { 
            name: "Marketplace", 
            spend: 4523, 
            impressions: 99000, 
            ctr: 2.5, 
            conversions: 124, 
            roas: 2.2,
            revenue: 9951
          },
          { 
            name: "Reels", 
            spend: 4523, 
            impressions: 99000, 
            ctr: 3.2, 
            conversions: 125, 
            roas: 3.1,
            revenue: 14021
          }
        ]
      },
      instagram: { 
        spend: 38750, 
        conversions: 982,
        revenue: 108500,
        impressions: 1080000,
        ctr: 3.1,
        cpc: 1.16,
        cpa: 39.4,
        roas: 2.8,
        placements: [
          { 
            name: "Feed", 
            spend: 15500, 
            impressions: 432000, 
            ctr: 3.4, 
            conversions: 393, 
            roas: 2.9,
            revenue: 44950
          },
          { 
            name: "Stories", 
            spend: 13562, 
            impressions: 378000, 
            ctr: 2.8, 
            conversions: 344, 
            roas: 2.7,
            revenue: 36617
          },
          { 
            name: "Reels", 
            spend: 7750, 
            impressions: 216000, 
            ctr: 3.3, 
            conversions: 196, 
            roas: 3.1,
            revenue: 24025
          },
          { 
            name: "Explore", 
            spend: 1938, 
            impressions: 54000, 
            ctr: 2.7, 
            conversions: 49, 
            roas: 2.3,
            revenue: 4457
          }
        ]
      }
    },

    adSets: [
      { id: 1, name: "Women's Collection", spend: 14500, conversions: 420, roas: 3.4, platform: "facebook" },
      { id: 2, name: "Men's Collection", spend: 10000, conversions: 260, roas: 2.9, platform: "facebook" },
      { id: 3, name: "New Arrivals", spend: 18700, conversions: 420, roas: 2.7, platform: "instagram" },
      { id: 4, name: "Special Offers", spend: 12600, conversions: 310, roas: 2.9, platform: "instagram" },
      { id: 5, name: "Brand Awareness", spend: 28180, conversions: 817, roas: 1.8, platform: "both" }
    ],
    dailyData: null
  });

  const displayDailyData = useMemo(() => {
    const bid = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
    if (YATAS_GROUP_BRAND_IDS.has(bid) && yatasCampaignFilter !== 'all') {
      if (filteredMetaCampaignRows.length > 0) {
        return processDailyTrendFromCampaignRows(filteredMetaCampaignRows);
      }
      if (accountFilteredCampaignRows.length > 0 && filteredMetaCampaignRows.length === 0) {
        return null;
      }
    }
    // Account filter active but no yatas filter — recompute daily from account-filtered rows
    if (metaAccountFilter !== 'all' && accountFilteredCampaignRows.length > 0) {
      return processDailyTrendFromCampaignRows(accountFilteredCampaignRows);
    }
    return data.dailyData;
  }, [filteredMetaCampaignRows, yatasCampaignFilter, data.dailyData, accountFilteredCampaignRows, metaAccountFilter]);

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        setLoading(true);
        
        // Get the active brand ID from local storage - use currentBrandId as seen in GoogleAds.js
        const brandId = localStorage.getItem('currentBrandId');
        
        if (!brandId) {
          console.error('No active brand ID found in localStorage');
          setLoading(false);
          return;
        }
        
        // Check if this is the special brand that needs dummy data
        if (brandId === DUMMY_DATA_BRAND_ID) {
          console.log('Using dummy data for special brand:', brandId);
          setRawMetaCampaignRows([]);

          // Set dummy placement data
          setMetaData(generateDummyMetaData());
          
          // Set dummy campaigns
          setCampaigns(generateDummyCampaigns());
          
          // Set dummy ad sets
          setAdSets(generateDummyAdSets());
          
          // Set dummy ads
          setAds(generateDummyAds());
          
          // Set dummy daily data
          setData(prevData => ({
            ...prevData,
            dailyData: generateDummyDailyData()
          }));

          setTimeout(() => setLoading(false), 800);
          return;
        }
        
        // Define the date range parameter
        const dateRange = selectedDateRange === 'today' ? 'today' :
                          selectedDateRange === 'yesterday' ? 'yesterday' :
                          selectedDateRange === '7' ? 'last7' : 
                          selectedDateRange === '30' ? 'last30' :
                          selectedDateRange === 'thisMonth' ? 'thisMonth' :
                          selectedDateRange === 'lastMonth' ? 'lastMonth' :
                          selectedDateRange === 'custom' ? 'custom' : 'last30';
        
        console.log(`Fetching Meta data for brand ID: ${brandId} with date range: ${dateRange}`);
        
        // Build URLs with custom date parameters if needed
        let placementUrl = `https://api.glowytics.com/api/google-ads/meta-placement-performance?dateRange=${dateRange}`;
        let campaignUrl = `https://api.glowytics.com/api/google-ads/meta-campaign-performance?dateRange=${dateRange}`;
        
        if (selectedAccountId && selectedAccountId !== 'all') {
          placementUrl += `&accountId=${selectedAccountId}`;
          campaignUrl += `&accountId=${selectedAccountId}`;
        }

        if (dateRange === 'custom' && customStartDate && customEndDate) {
          const startDateStr = formatLocalDateYMD(customStartDate);
          const endDateStr = formatLocalDateYMD(customEndDate);
          placementUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
          campaignUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
          console.log(`Using custom date range: ${startDateStr} to ${endDateStr}`);
        }

        // Paralel olarak placement, campaign, daily summary verilerini çek
        const [placementResponse, campaignResponse] = await Promise.all([
          // Placement performance endpoint
          axios.get(placementUrl, {
            withCredentials: true,
            headers: {
              'X-Brand-ID': brandId
            }
          }),
          // Campaign performance endpoint
          axios.get(campaignUrl, {
            withCredentials: true,
            headers: {
              'X-Brand-ID': brandId
            }
          })
        ]);
        
        // Log the data to console
        console.log('Meta placement performance data:', placementResponse.data);
        console.log('Meta campaign performance data:', campaignResponse.data);
        
        // Store the data in state with startTransition for non-blocking UI
        startTransition(() => {
          setMetaData(placementResponse.data);
        });
        
        const campaignData = Array.isArray(campaignResponse.data) ? campaignResponse.data : [];

        startTransition(() => {
          setRawMetaCampaignRows(campaignData);
        });

        const dailyTrendData = processDailyTrendFromCampaignRows(campaignData);
        console.log('Processed daily trend data:', dailyTrendData);
        
        // Update the data state with daily trend data
        startTransition(() => {
          setData(prevData => ({
            ...prevData,
            dailyData: dailyTrendData
          }));
        });
        
        // Set loading to false - no artificial delay needed
        setLoading(false);
        
      } catch (error) {
        console.error('Error fetching Meta campaign data:', error);
        setLoading(false);
      }
    };

    // Custom date range seçildiğinde sadece her iki tarih de varsa API çağır
    if (selectedDateRange === 'custom') {
      if (customStartDate && customEndDate) {
        fetchMetaData();
      }
      // Custom date seçilmiş ama tarihler eksikse API çağırma
    } else {
      // Normal date range'ler için direkt çağır
      fetchMetaData();
    }
  }, [selectedDateRange, customStartDate, customEndDate, selectedAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (value) => {
    return '₺' + value.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatCurrencyNoDecimals = (value) => {
    return '₺' + Math.round(value).toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatNumber = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  const getGrowthIndicator = (value) => {
    if (value > 0) return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (value < 0) return <ArrowDown className="w-4 h-4 text-red-500" />;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f8ff]/70">
        <div className="loading-logo-container">
          <img src="/logoicon.png" alt="Loading" className="loading-logo" />
        </div>
      </div>
    );
  }

  const totalSpend = data.summary.spend;
  const totalRevenue = data.summary.revenue;
  const totalRoas = data.summary.roas;

  return (
    <div className="p-6 space-y-6">
      {/* Header with Filters */}
      <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <img 
              src="/6033716.png" 
              alt="Meta Ads Logo" 
              className="w-8 h-8 object-contain"
            />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Meta Ads Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">Real-time performance overview</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <AccountSwitcher platform="META_ADS" />
          {metaAccountOptions.length >= 2 && (
            <select
              value={metaAccountFilter}
              onChange={(e) => setMetaAccountFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[160px]"
            >
              <option value="all">All Accounts</option>
              {metaAccountOptions.map(id => (
                <option key={id} value={id}>{accountNameMap[id] || id}</option>
              ))}
            </select>
          )}
          <select
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]"
            value={selectedDateRange}
            onChange={(e) => {
              setSelectedDateRange(e.target.value);
              // Custom seçildiğinde tarihler sıfırla
              if (e.target.value === 'custom') {
                setCustomStartDate(null);
                setCustomEndDate(null);
              }
            }}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="custom">Custom Range</option>
          </select>

          {showYatasCampaignFilter && (
            <select
              value={yatasCampaignFilter}
              onChange={(e) => setYatasCampaignFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
              aria-label="Campaign type by name"
            >
              <option value="all">All Campaigns</option>
              <option value="performance">Performance campaigns</option>
              <option value="branding">Branding campaigns</option>
            </select>
          )}

          {/* Custom Date Range Button */}
          {selectedDateRange === 'custom' && (
            <div className="relative">
              <button 
                onClick={() => setIsCustomDateOpen(!isCustomDateOpen)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                {customStartDate && customEndDate ? (
                  `${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`
                ) : (
                  'Select Dates'
                )}
              </button>
              
              {isCustomDateOpen && (
                <div className="absolute right-0 mt-2 z-50">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold mb-3">Select Date Range</h3>
                    <DatePicker
                      selected={customStartDate}
                      onChange={(dates) => {
                        const [start, end] = dates;
                        setCustomStartDate(start);
                        setCustomEndDate(end);
                        if (start && end) {
                          setIsCustomDateOpen(false);
                          console.log('Custom date range selected:', start, end);
                          // API çağrısı useEffect'te otomatik olacak, her iki tarih de seçildiği için
                        }
                      }}
                      startDate={customStartDate}
                      endDate={customEndDate}
                      selectsRange
                      inline
                      calendarClassName="bg-white"
                      monthClassName="text-gray-800"
                      dayClassName={date =>
                        date >= customStartDate && date <= customEndDate
                          ? "bg-blue-50 text-blue-600"
                          : undefined
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Summary */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="p-4 rounded-xl !bg-blue-600 text-white">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-white/80 text-sm mb-1">Total Spend</p>
              <p className="text-2xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-white/20 animate-pulse rounded"></span>
                ) : (
                  formatCurrencyNoDecimals(computedMetrics.totalSpend || 0)
                )}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Revenue</p>
              <p className="text-2xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  formatCurrencyNoDecimals(computedMetrics.totalPurchaseValue || 0)
                )}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">ROAS</p>
              <p className="text-2xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  `${computedMetrics.roas.toFixed(1)}x`
                )}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Conversions</p>
              <p className="text-2xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  computedMetrics.totalPurchaseConversions.toLocaleString()
                )}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">CPA</p>
              <p className="text-2xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  formatCurrency(computedMetrics.cpa || 0)
                )}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <Hash className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Performance Trends + Budget Utilization - Grid layout like Google Ads */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Performance Chart - 2 cols (Google Ads style) */}
        <Card className="rounded-xl overflow-hidden lg:col-span-2">
          <div className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <h3 className="text-sm font-medium text-gray-800">Performance Trends</h3>
            <div className="flex items-center gap-2">
              {displayDailyData && (
                <>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-100 text-xs font-medium text-blue-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Spend ₺{(displayDailyData.spend?.reduce((a, b) => a + (b || 0), 0) || 0).toLocaleString('tr-TR')}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-100 text-xs font-medium text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Revenue ₺{(displayDailyData.revenue?.reduce((a, b) => a + (b || 0), 0) || 0).toLocaleString('tr-TR')}
                  </span>
                </>
              )}
              <select className="px-2 py-1 border rounded-lg text-xs ml-1">
                <option value="spend_revenue">Spend vs Revenue</option>
                <option value="clicks">Clicks</option>
                <option value="impressions">Impressions</option>
              </select>
            </div>
          </div>
          
          <div className="px-4 pb-4">
            <div className="h-[220px] md:h-[260px] -mx-1">
              {displayDailyData ? (
                <LineChart 
                  data={{
                    labels: displayDailyData.labels,
                    datasets: [
                      {
                        label: 'Spend',
                        data: displayDailyData.spend,
                        yAxisID: 'y1',
                        borderColor: '#3b82f6',
                        backgroundColor: function(context) {
                          const chart = context.chart;
                          const { ctx, chartArea } = chart;
                          if (!chartArea) return 'transparent';
                          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                          gradient.addColorStop(0, 'rgba(59, 130, 246, 0.35)');
                          gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
                          return gradient;
                        },
                        fill: true,
                        tension: 0.35,
                        borderWidth: 2.5,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointHoverBackgroundColor: '#fff',
                        pointHoverBorderColor: '#3b82f6',
                        pointHoverBorderWidth: 2,
                        spanGaps: true
                      },
                      {
                        label: 'Revenue',
                        data: displayDailyData.revenue,
                        yAxisID: 'y',
                        borderColor: '#10b981',
                        backgroundColor: function(context) {
                          const chart = context.chart;
                          const { ctx, chartArea } = chart;
                          if (!chartArea) return 'transparent';
                          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                          gradient.addColorStop(0, 'rgba(16, 185, 129, 0.35)');
                          gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
                          return gradient;
                        },
                        fill: true,
                        tension: 0.35,
                        borderWidth: 2.5,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointHoverBackgroundColor: '#fff',
                        pointHoverBorderColor: '#10b981',
                        pointHoverBorderWidth: 2,
                        spanGaps: true
                      }
                    ]
                  }}
                  height="100%"
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 4, right: 12, bottom: 4, left: 12 } },
                    interaction: { mode: 'index', intersect: false },
                    scales: {
                      x: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: { maxRotation: 0, maxTicksLimit: 8, font: { size: 10 }, color: '#94a3b8' }
                      },
                      y: {
                        position: 'left',
                        beginAtZero: false,
                        border: { display: false },
                        ticks: {
                          callback: (value) => '₺' + (value / 1000) + 'K',
                          font: { size: 10 },
                          color: '#10b981',
                          maxTicksLimit: 5
                        },
                        grid: { color: 'rgba(16, 185, 129, 0.15)', drawTicks: false }
                      },
                      y1: {
                        position: 'right',
                        beginAtZero: false,
                        border: { display: false },
                        grid: { drawOnChartArea: false },
                        ticks: {
                          callback: (value) => '₺' + (value / 1000) + 'K',
                          font: { size: 10 },
                          color: '#3b82f6',
                          maxTicksLimit: 5
                        }
                      }
                    },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(255,255,255,0.98)',
                        titleColor: '#1e293b',
                        bodyColor: '#475569',
                        borderColor: '#e2e8f0',
                        borderWidth: 1,
                        padding: 10,
                        boxPadding: 4,
                        cornerRadius: 8,
                        titleFont: { size: 12, weight: '600' },
                        bodyFont: { size: 11 },
                        usePointStyle: true,
                        callbacks: {
                          title: (items) => items[0]?.label || '',
                          label: (context) => {
                            const label = context.dataset.label || '';
                            const value = context.raw || 0;
                            return `${label}: ₺${Math.round(value).toLocaleString('tr-TR')}`;
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full">
                  <BarChart3 className="w-12 h-12 text-gray-300 mb-2" />
                  <h3 className="text-base font-medium text-gray-600 mb-1">No data available</h3>
                  <p className="text-sm text-gray-400 text-center max-w-xs">
                    No performance trend data for the selected date range.
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Budget Utilization Card - 1 col */}
        <Card className="rounded-xl overflow-hidden flex flex-col min-h-[280px]">
          <div className="flex justify-between items-center p-4 border-b flex-shrink-0">
            <h3 className="text-sm font-medium text-gray-800">Budget Utilization</h3>
            <div className="p-1.5 bg-blue-50 rounded-full border border-blue-100">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            {(computedMetrics.totalSpend !== undefined && computedMetrics.totalSpend !== null) ? (
              (() => {
                const today = new Date();
                const currentYear = today.getFullYear();
                const currentMonth = today.getMonth();
                const currentDay = today.getDate();
                const totalDaysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
                const remainingDays = totalDaysInMonth - currentDay;
                const totalSpent = computedMetrics.totalSpend || 0;
                const hasTarget = targetBudget != null && Number(targetBudget) > 0;
                const spentPct = hasTarget ? Math.min(Math.round((totalSpent / Number(targetBudget)) * 100), 100) : 0;
                const remainingBudget = hasTarget ? Math.max(0, Number(targetBudget) - totalSpent) : 0;
                const dailyTarget = hasTarget && remainingDays > 0 ? remainingBudget / remainingDays : 0;
                const ringColor = !hasTarget ? '#9ca3af' : spentPct > 80 ? '#ef4444' : spentPct > 50 ? '#f59e0b' : '#3b82f6';
                const circumference = 2 * Math.PI * 36;
                const strokeDash = (spentPct / 100) * circumference;

                const formatBudgetNum = (val) => '₺' + Math.round(val).toLocaleString('tr-TR');

                return (
                  <div className="flex-1 flex flex-col justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="relative flex-shrink-0">
                        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 84 84">
                          <circle cx="42" cy="42" r="36" fill="none" stroke="#f3f4f6" strokeWidth="6" />
                          <circle cx="42" cy="42" r="36" fill="none" stroke={ringColor} strokeWidth="6" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference - strokeDash} className="transition-all duration-500" />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums" style={{ color: ringColor }}>{spentPct}%</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">{today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
                        <p className="text-2xl font-bold tabular-nums text-gray-900 mt-0.5">{formatBudgetNum(totalSpent)}</p>
                        <div className="text-[11px] text-gray-500 mt-1">
                          {isEditingBudget || !hasTarget ? (
                            <span className="flex items-center gap-1.5">
                              <span className="text-base font-semibold text-gray-700">₺</span>
                              <input
                                type="text"
                                className="w-40 border border-blue-300 rounded-md px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 placeholder-gray-400"
                                defaultValue={hasTarget ? Number(targetBudget).toLocaleString('tr-TR') : ''}
                                placeholder={hasTarget ? '' : 'Hedef bütçe gir'}
                                disabled={savingTargetBudget}
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                  } else if (e.key === 'Escape') {
                                    e.target.dataset.cancelled = '1';
                                    setIsEditingBudget(false);
                                  }
                                }}
                                onBlur={async (e) => {
                                  if (e.target.dataset.cancelled === '1') {
                                    setIsEditingBudget(false);
                                    return;
                                  }
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  const numValue = parseInt(value, 10);
                                  if (!isNaN(numValue) && numValue > 0 && numValue !== Number(targetBudget)) {
                                    await saveMonthlyTarget(numValue);
                                  }
                                  setIsEditingBudget(false);
                                }}
                                autoFocus={isEditingBudget}
                              />
                              {!hasTarget && (
                                <span className="text-[10px] text-gray-400 italic">Enter ile kaydet</span>
                              )}
                            </span>
                          ) : (
                            <span className="cursor-pointer hover:text-blue-600 transition-colors" onClick={() => setIsEditingBudget(true)}>
                              of {formatBudgetNum(Number(targetBudget))} target
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${spentPct}%`, backgroundColor: ringColor }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 flex-shrink-0">
                      <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-3">
                        <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">Daily target</p>
                        <p className="text-lg font-bold tabular-nums text-gray-900 mt-0.5">{formatBudgetNum(dailyTarget)}</p>
                      </div>
                      <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-3">
                        <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">Remaining</p>
                        <p className="text-lg font-bold tabular-nums text-gray-900 mt-0.5">{formatBudgetNum(remainingBudget)}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{remainingDays} days left</p>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="flex flex-col items-center justify-center flex-1">
                <DollarSign className="w-12 h-12 text-gray-400 mb-2" />
                <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  No budget data found for the selected date range.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Platform Performance Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Facebook Card */}
        <Card className="rounded-xl overflow-hidden">
          <div className="p-4 bg-blue-50 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <img 
                    src="/facebook.png" 
                    alt="Facebook" 
                    className="w-6 h-6"
                  />
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">Facebook</h3>
                  <p className="text-xs text-gray-500">
                    {loading ? 
                      <span className="inline-block w-16 h-3 bg-gray-100 animate-pulse rounded"></span> :
                      `${formatNumber(computedMetrics.platforms?.facebook?.impressions || 0)} impressions`
                    }
                  </p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${
                loading ? 'bg-gray-100 text-gray-400' :
                (computedMetrics.platforms?.facebook?.roas >= 2 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700')
              }`}>
                {loading ? 
                  <span className="inline-block w-14 h-3 bg-gray-200 animate-pulse rounded"></span> :
                  `ROAS: ${(computedMetrics.platforms?.facebook?.roas || 0).toFixed(1)}x`
                }
              </span>
            </div>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-5 gap-3 mb-4">
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">Spend</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-14 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    formatCurrencyNoDecimals(computedMetrics.platforms?.facebook?.spend || 0)
                  }
                </p>
              </div>
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">Revenue</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-14 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    formatCurrencyNoDecimals(computedMetrics.platforms?.facebook?.purchaseValue || 0)
                  }
                </p>
              </div>
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">Conversions</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-10 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    (computedMetrics.platforms?.facebook?.purchaseConversions || 0).toLocaleString()
                  }
                </p>
              </div>
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">CTR</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-8 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    `${(computedMetrics.platforms?.facebook?.ctr || 0).toFixed(2)}%`
                  }
                </p>
              </div>
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">CPA</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-14 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    formatCurrency(computedMetrics.platforms?.facebook?.cpa || 0)
                  }
                </p>
              </div>
            </div>
            
            {/* Facebook Placements */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-medium text-gray-700">Placement Performance</h4>
                <select 
                  className="text-xs border rounded px-2 py-1 text-gray-600"
                  value={fbPlacementSort}
                  onChange={(e) => setFbPlacementSort(e.target.value)}
                >
                  <option value="spend">By Spend</option>
                  <option value="roas">By ROAS</option>
                  <option value="ctr">By CTR</option>
                </select>
              </div>
              
              <div className="max-h-[520px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 space-y-2">
                {loading ? (
                  // Show placeholders when loading
                  Array(5).fill(0).map((_, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-md bg-gray-100"></div>
                          <span className="inline-block w-24 h-4 bg-gray-100 animate-pulse rounded"></span>
                        </div>
                        <span className="inline-block w-10 h-4 bg-gray-100 animate-pulse rounded"></span>
                      </div>
                      <div className="grid grid-cols-5 gap-1">
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                      </div>
                      <div className="mt-1.5 h-1 bg-gray-100 rounded-full"></div>
                    </div>
                  ))
                ) : (
                  // Show actual placements when data is loaded - sorted by selected option
                  [...(computedMetrics.platforms?.facebook?.placements || [])].sort((a, b) => {
                    if (fbPlacementSort === 'roas') return b.roas - a.roas;
                    if (fbPlacementSort === 'ctr') return b.ctr - a.ctr;
                    return b.spend - a.spend; // default: spend
                  }).map((placement, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center">
                            {placement.name.toLowerCase().includes("feed") && <Image className="w-3 h-3 text-blue-600" />}
                            {placement.name.toLowerCase().includes("story") && <Play className="w-3 h-3 text-blue-600" />}
                            {placement.name.toLowerCase().includes("reel") && <Play className="w-3 h-3 text-blue-600" />}
                            {placement.name.toLowerCase().includes("market") && <ShoppingCart className="w-3 h-3 text-blue-600" />}
                            {!placement.name.toLowerCase().includes("feed") && 
                             !placement.name.toLowerCase().includes("story") && 
                             !placement.name.toLowerCase().includes("reel") && 
                             !placement.name.toLowerCase().includes("market") && 
                             <Target className="w-3 h-3 text-blue-600" />}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs font-medium">Facebook {placement.name}</span>
                            <span className="text-xs text-blue-600 font-semibold">
                              {((placement.spend / computedMetrics.platforms.facebook.spend) * 100).toFixed(1)}% of spend
                            </span>
                          </div>
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          placement.roas >= 3 ? 'bg-green-100 text-green-700' : 
                          placement.roas >= 2 ? 'bg-blue-100 text-blue-700' : 
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {placement.roas.toFixed(1)}x
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-5 gap-1 text-xs">
                        <div>
                          <span className="text-gray-500">Spend</span>
                          <div className="font-medium">{formatCurrencyNoDecimals(placement.spend)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Impr.</span>
                          <div className="font-medium">{formatNumber(placement.impressions)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Revenue</span>
                          <div className="font-medium text-green-600">{formatCurrencyNoDecimals(placement.revenue)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">CTR</span>
                          <div className="font-medium">{placement.ctr.toFixed(2)}%</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Conv</span>
                          <div className="font-medium">{placement.conversions}</div>
                        </div>
                      </div>
                      
                      {/* Harcama dağılımını gösteren bar */}
                      <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full" 
                          style={{ width: `${(placement.spend / computedMetrics.platforms.facebook.spend * 100).toFixed(1)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Card>
        
        {/* Instagram Card */}
        <Card className="rounded-xl overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 via-purple-500 to-orange-400 rounded-xl flex items-center justify-center">
                  <img 
                    src="/instagram.png" 
                    alt="Instagram" 
                    className="w-6 h-6"
                  />
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">Instagram</h3>
                  <p className="text-xs text-gray-500">
                    {loading ? 
                      <span className="inline-block w-16 h-3 bg-gray-100 animate-pulse rounded"></span> :
                      `${formatNumber(computedMetrics.platforms?.instagram?.impressions || 0)} impressions`
                    }
                  </p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${
                loading ? 'bg-gray-100 text-gray-400' :
                (computedMetrics.platforms?.instagram?.roas >= 2 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700')
              }`}>
                {loading ? 
                  <span className="inline-block w-14 h-3 bg-gray-200 animate-pulse rounded"></span> :
                  `ROAS: ${(computedMetrics.platforms?.instagram?.roas || 0).toFixed(1)}x`
                }
              </span>
            </div>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-5 gap-3 mb-4">
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">Spend</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-14 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    formatCurrencyNoDecimals(computedMetrics.platforms?.instagram?.spend || 0)
                  }
                </p>
              </div>
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">Revenue</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-14 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    formatCurrencyNoDecimals(computedMetrics.platforms?.instagram?.purchaseValue || 0)
                  }
                </p>
              </div>
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">Conversions</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-10 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    (computedMetrics.platforms?.instagram?.purchaseConversions || 0).toLocaleString()
                  }
                </p>
              </div>
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">CTR</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-8 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    `${(computedMetrics.platforms?.instagram?.ctr || 0).toFixed(2)}%`
                  }
                </p>
              </div>
              <div className="text-center p-2">
                <p className="text-xs text-gray-500 mb-1">CPA</p>
                <p className="text-base font-semibold">
                  {loading ? 
                    <span className="inline-block w-14 h-5 bg-gray-100 animate-pulse rounded"></span> :
                    formatCurrency(computedMetrics.platforms?.instagram?.cpa || 0)
                  }
                </p>
              </div>
            </div>
            
            {/* Instagram Placements */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-medium text-gray-700">Placement Performance</h4>
                <select 
                  className="text-xs border rounded px-2 py-1 text-gray-600"
                  value={igPlacementSort}
                  onChange={(e) => setIgPlacementSort(e.target.value)}
                >
                  <option value="spend">By Spend</option>
                  <option value="roas">By ROAS</option>
                  <option value="ctr">By CTR</option>
                </select>
              </div>
              
              <div className="max-h-[520px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 space-y-2">
                {loading ? (
                  // Show placeholders when loading
                  Array(5).fill(0).map((_, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-md bg-gray-100"></div>
                          <span className="inline-block w-24 h-4 bg-gray-100 animate-pulse rounded"></span>
                        </div>
                        <span className="inline-block w-10 h-4 bg-gray-100 animate-pulse rounded"></span>
                      </div>
                      <div className="grid grid-cols-5 gap-1">
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                        <span className="inline-block w-full h-3 bg-gray-100 animate-pulse rounded"></span>
                      </div>
                      <div className="mt-1.5 h-1 bg-gray-100 rounded-full"></div>
                    </div>
                  ))
                ) : (
                  // Show actual placements when data is loaded - sorted by selected option
                  [...(computedMetrics.platforms?.instagram?.placements || [])].sort((a, b) => {
                    if (igPlacementSort === 'roas') return b.roas - a.roas;
                    if (igPlacementSort === 'ctr') return b.ctr - a.ctr;
                    return b.spend - a.spend; // default: spend
                  }).map((placement, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-md bg-purple-100 flex items-center justify-center">
                            {placement.name.toLowerCase().includes("feed") && <Image className="w-3 h-3 text-purple-600" />}
                            {placement.name.toLowerCase().includes("story") && <Play className="w-3 h-3 text-purple-600" />}
                            {placement.name.toLowerCase().includes("reel") && <Play className="w-3 h-3 text-purple-600" />}
                            {placement.name.toLowerCase().includes("explore") && <Target className="w-3 h-3 text-purple-600" />}
                            {!placement.name.toLowerCase().includes("feed") && 
                             !placement.name.toLowerCase().includes("story") && 
                             !placement.name.toLowerCase().includes("reel") && 
                             !placement.name.toLowerCase().includes("explore") && 
                             <Target className="w-3 h-3 text-purple-600" />}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs font-medium">Instagram {placement.name}</span>
                            <span className="text-xs text-purple-600 font-semibold">
                              {((placement.spend / computedMetrics.platforms.instagram.spend) * 100).toFixed(1)}% of spend
                            </span>
                          </div>
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          placement.roas >= 3 ? 'bg-green-100 text-green-700' : 
                          placement.roas >= 2 ? 'bg-blue-100 text-blue-700' : 
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {placement.roas.toFixed(1)}x
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-5 gap-1 text-xs">
                        <div>
                          <span className="text-gray-500">Spend</span>
                          <div className="font-medium">{formatCurrencyNoDecimals(placement.spend)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Impr.</span>
                          <div className="font-medium">{formatNumber(placement.impressions)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Revenue</span>
                          <div className="font-medium text-green-600">{formatCurrencyNoDecimals(placement.revenue)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">CTR</span>
                          <div className="font-medium">{placement.ctr.toFixed(2)}%</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Conv</span>
                          <div className="font-medium">{placement.conversions}</div>
                        </div>
                      </div>
                      
                      {/* Harcama dağılımını gösteren bar */}
                      <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500 rounded-full" 
                          style={{ width: `${(placement.spend / computedMetrics.platforms.instagram.spend * 100).toFixed(1)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Section Navigation */}
      <div className="flex items-center gap-1 bg-white p-1 rounded-xl shadow-sm border">
        <button
          onClick={() => setActiveSection('campaigns')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeSection === 'campaigns'
              ? 'bg-blue-500 text-white shadow-sm'
              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          Campaigns
          <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">
            {loading ? '...' : campaigns.length}
          </span>
        </button>
        
        <button
          onClick={() => setActiveSection('adsets')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeSection === 'adsets'
              ? 'bg-blue-500 text-white shadow-sm'
              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          Ad Sets
          <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">
            {loading ? '...' : adSets.length}
          </span>
        </button>
        
        <button
          onClick={() => setActiveSection('ads')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeSection === 'ads'
              ? 'bg-blue-500 text-white shadow-sm'
              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Ads
          <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">
            {loading ? '...' : ads.length}
          </span>
        </button>
      </div>

      {/* Dynamic Table Based on Active Section */}
      <Card className="rounded-xl overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-800">
              {activeSection === 'campaigns' ? 'Campaign Performance' :
               activeSection === 'adsets' ? 'Ad Set Performance' : 'Ad Performance'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {loading ? `Loading ${activeSection}...` : 
               `${activeSection === 'campaigns' ? campaigns.length : 
                 activeSection === 'adsets' ? adSets.length : ads.length} ${activeSection} found`}
            </p>
          </div>
        </div>
        
                {/* Scrollable Table Area */}
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {activeSection === 'campaigns' ? 'Campaign' : 
                   activeSection === 'adsets' ? 'Ad Set' : 'Ad'}
                </th>
                {activeSection === 'adsets' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                )}
                {activeSection === 'ads' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ad Set</th>
                  </>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spend</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPC</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversions</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ROAS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                // Loading state için placeholder rows
                Array(10).fill(0).map((_, index) => (
                  <tr key={index} className="hover:bg-gray-50 animate-pulse">
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        <div className="w-3 h-3 bg-gray-200 rounded-full animate-pulse"></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                    </td>
                    {activeSection === 'adsets' && (
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </td>
                    )}
                    {activeSection === 'ads' && (
                      <>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="h-4 bg-gray-200 rounded w-24"></div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="h-4 bg-gray-200 rounded w-24"></div>
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-6 bg-gray-200 rounded w-12"></div>
                    </td>
                  </tr>
                ))
              ) : (
                // Dinamik veri gösterimi - section'a göre
                (() => {
                  const currentData = activeSection === 'campaigns' ? campaigns : 
                                    activeSection === 'adsets' ? adSets : ads;
                  
                  return currentData.length > 0 ? (
                    currentData.map(item => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center">
                            <div className="w-3 h-3 bg-green-700 rounded-full"></div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">
                          {item.name || `Unknown ${activeSection.slice(0, -1)}`}
                        </td>
                        {activeSection === 'adsets' && (
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.campaignName || 'Unknown Campaign'}
                          </td>
                        )}
                        {activeSection === 'ads' && (
                          <>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {item.campaignName || 'Unknown Campaign'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {item.adsetName || 'Unknown Ad Set'}
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3 text-sm text-gray-600">
                          ₺{Math.round(item.spend).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          ₺{Math.round(item.revenue).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatNumber(item.impressions)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatNumber(item.clicks)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatCurrency(item.cpc)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.conversions}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.roas >= 3 ? 'bg-green-100 text-green-700' : 
                            item.roas >= 2 ? 'bg-blue-100 text-blue-700' : 
                            item.roas >= 1 ? 'bg-yellow-100 text-yellow-700' : 
                            'bg-red-100 text-red-700'
                          }`}>
                            {item.roas.toFixed(1)}x
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    // No data state
                    <tr>
                      <td colSpan={activeSection === 'campaigns' ? 9 : activeSection === 'adsets' ? 10 : 11} className="px-4 py-8 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <span>No {activeSection} data available for the selected period</span>
                        </div>
                      </td>
                    </tr>
                  );
                })()
              )}
            </tbody>
          </table>
        </div>
        
        {/* Fixed Total Footer */}
        {!loading && (
          <div className="border-t-2 border-gray-200 bg-gray-50 overflow-x-auto">
            <table className="w-full">
              <tbody>
                <tr className="font-semibold">
                  {/* Status Column */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center">
                      <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                    </div>
                  </td>
                  
                  {/* Campaign/AdSet/Ad Name Column */}
                  <td className="px-4 py-3 text-sm font-bold text-gray-800">
                    Total ({activeSection === 'campaigns' ? campaigns.length : 
                           activeSection === 'adsets' ? adSets.length : ads.length})
                  </td>
                  
                  {/* Campaign Column (only for adsets) */}
                  {activeSection === 'adsets' && (
                    <td className="px-4 py-3 text-sm text-gray-600">-</td>
                  )}
                  
                  {/* Campaign + Ad Set Columns (only for ads) */}
                  {activeSection === 'ads' && (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-600">-</td>
                      <td className="px-4 py-3 text-sm text-gray-600">-</td>
                    </>
                  )}
                  
                  {/* Spend Column */}
                  <td className="px-4 py-3 text-sm font-bold text-gray-800">
                    {(() => {
                      const currentData = activeSection === 'campaigns' ? campaigns : 
                                        activeSection === 'adsets' ? adSets : ads;
                      const totalSpend = currentData.reduce((sum, item) => sum + (item.spend || 0), 0);
                      return `₺${Math.round(totalSpend).toLocaleString()}`;
                    })()}
                  </td>
                  
                  {/* Revenue Column */}
                  <td className="px-4 py-3 text-sm font-bold text-green-600">
                    {(() => {
                      const currentData = activeSection === 'campaigns' ? campaigns : 
                                        activeSection === 'adsets' ? adSets : ads;
                      const totalRevenue = currentData.reduce((sum, item) => sum + (item.revenue || 0), 0);
                      return `₺${Math.round(totalRevenue).toLocaleString()}`;
                    })()}
                  </td>
                  
                  {/* Impressions Column */}
                  <td className="px-4 py-3 text-sm font-bold text-gray-800">
                    {(() => {
                      const currentData = activeSection === 'campaigns' ? campaigns : 
                                        activeSection === 'adsets' ? adSets : ads;
                      const totalImpressions = currentData.reduce((sum, item) => sum + (item.impressions || 0), 0);
                      return formatNumber(totalImpressions);
                    })()}
                  </td>
                  
                  {/* Clicks Column */}
                  <td className="px-4 py-3 text-sm font-bold text-gray-800">
                    {(() => {
                      const currentData = activeSection === 'campaigns' ? campaigns : 
                                        activeSection === 'adsets' ? adSets : ads;
                      const totalClicks = currentData.reduce((sum, item) => sum + (item.clicks || 0), 0);
                      return formatNumber(totalClicks);
                    })()}
                  </td>
                  
                  {/* CPC Column */}
                  <td className="px-4 py-3 text-sm font-bold text-gray-800">
                    {(() => {
                      const currentData = activeSection === 'campaigns' ? campaigns : 
                                        activeSection === 'adsets' ? adSets : ads;
                      const totalSpend = currentData.reduce((sum, item) => sum + (item.spend || 0), 0);
                      const totalClicks = currentData.reduce((sum, item) => sum + (item.clicks || 0), 0);
                      const avgCPC = totalClicks > 0 ? totalSpend / totalClicks : 0;
                      return formatCurrency(avgCPC);
                    })()}
                  </td>
                  
                  {/* Conversions Column */}
                  <td className="px-4 py-3 text-sm font-bold text-gray-800">
                    {(() => {
                      const currentData = activeSection === 'campaigns' ? campaigns : 
                                        activeSection === 'adsets' ? adSets : ads;
                      const totalConversions = currentData.reduce((sum, item) => sum + (item.conversions || 0), 0);
                      return totalConversions.toLocaleString();
                    })()}
                  </td>
                  
                  {/* ROAS Column */}
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      (() => {
                        const currentData = activeSection === 'campaigns' ? campaigns : 
                                          activeSection === 'adsets' ? adSets : ads;
                        const totalSpend = currentData.reduce((sum, item) => sum + (item.spend || 0), 0);
                        const totalRevenue = currentData.reduce((sum, item) => sum + (item.revenue || 0), 0);
                        const totalROAS = totalSpend > 0 ? totalRevenue / totalSpend : 0;
                        return totalROAS >= 3 ? 'bg-green-100 text-green-700' : 
                               totalROAS >= 2 ? 'bg-blue-100 text-blue-700' : 
                               totalROAS >= 1 ? 'bg-yellow-100 text-yellow-700' : 
                               'bg-red-100 text-red-700';
                      })()
                    }`}>
                      {(() => {
                        const currentData = activeSection === 'campaigns' ? campaigns : 
                                          activeSection === 'adsets' ? adSets : ads;
                        const totalSpend = currentData.reduce((sum, item) => sum + (item.spend || 0), 0);
                        const totalRevenue = currentData.reduce((sum, item) => sum + (item.revenue || 0), 0);
                        const totalROAS = totalSpend > 0 ? totalRevenue / totalSpend : 0;
                        return `${totalROAS.toFixed(1)}x`;
                      })()}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>


    </div>
  );
} 